import { supabase } from '@/lib/supabase';

export interface ParsedDebt {
  name: string;
  amount: number;
  description: string;
}

/**
 * Compresse une photo (carnet) côté navigateur et la convertit en data URI JPEG.
 * On réduit la taille pour limiter la charge envoyée à l'IA et rester privé :
 * les photos ne sont pas stockées sur le serveur.
 */
export async function compressImageToDataUrl(file: File, maxSide = 1400, quality = 0.75): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Lecture du fichier impossible'));
    reader.readAsDataURL(file);
  });

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Image invalide'));
    image.src = dataUrl;
  });

  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}

/** Envoie les photos à l'IA (fonction Backend) et récupère la liste des dettes. */
export async function runDebtImport(images: string[]): Promise<ParsedDebt[]> {
  const { data, error } = await supabase.functions.invoke('credit-debt-import', {
    body: { images },
  });

  if (error) {
    let message = error.message || "Impossible de contacter l'IA";
    try {
      const context = (error as unknown as { context?: Response }).context;
      if (context) {
        const body = (await context.json()) as { error?: string };
        if (body?.error) message = body.error;
      }
    } catch { /* message générique */ }
    throw new Error(message);
  }

  const payload = data as { debts?: ParsedDebt[]; error?: string } | null;
  if (!payload) throw new Error("Réponse vide de l'IA");
  if (payload.error) throw new Error(payload.error);
  return payload.debts || [];
}